import React, {ReactElement, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  MimeTypeFileExtensions,
  TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER
} from '@shared/constants/Mapping';
import {makeStyles, Theme, Tooltip} from '@material-ui/core';
import {
  APP_PRIMARY_COLOR,
  DEFAULT_GREY_COLOR,
  DEFAULT_WHITE_COLOR,
  DEFAULT_DOC_FILE_BACKGROUND_COLOR,
  DEFAULT_DOC_FILE_ICON_BACKGROUND_COLOR
} from 'src/style/variable';
import GetAppIcon from '@material-ui/icons/GetApp';
import Loader from '@components/framework/loader/Loader';
import {getMediaFile} from '@shared/services/CommonServices';
import AudioFileComponent from 'src/modules/admin/modules/Sms/components/AudioFileComponent';
import DocFileComponent from 'src/modules/admin/modules/Sms/components/DocFileComponent';
import 'react-photo-view/dist/react-photo-view.css';
import PhotoPreviewComponent from 'src/modules/admin/modules/Sms/components/PhotoPreviewComponent';
import ErrorCard from '@components/system/ErrorCard/ErrorCard';
import {getTicketAttachments} from '@shared/services/WorkflowServices';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import {PhotoSlider} from 'react-photo-view';
import {IAllPossibleTicketModules} from 'src/modules/admin/modules/TicketingSystem/hooks/useTicketingSystemPermission';
const useStyles = makeStyles((theme: Theme) => ({
  mediaHeader: {
    position: 'relative',
    maxWidth: 'fit-content'
  },
  content: {
    maxWidth: '20rem !important'
  },
  docFileContent: {
    maxWidth: '20rem !important',
    maxHeight: '10rem',
    display: 'flex',
    margin: '0.4rem 0 0 0.4rem'
  },
  docFileNameClass: {
    margin: '0.3rem'
  },
  docFileAvatarContent: {
    color: DEFAULT_WHITE_COLOR,
    backgroundColor: DEFAULT_DOC_FILE_ICON_BACKGROUND_COLOR,
    padding: '0.5em',
    fontSize: '0.7em',
    margin: '0.25rem 0.5rem 0 0',
    width: '35px',
    height: '35px'
  },
  renderS3FileContent: {
    maxWidth: '30rem !important',
    height: '20rem !important'
  },
  topSection: {
    display: 'flex',
    alignItems: 'center'
  },
  cancel: {
    marginLeft: ' auto',
    padding: '16px 24px',
    cursor: 'pointer'
  },
  downloadSection: {
    position: 'absolute',
    margin: '1em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    top: -1,
    right: 0
  },
  mediaSection: {
    // position: 'relative',
    // float: 'left'
    display: 'flex',
    alignItems: 'center'
  },
  audioMediaSection: {
    marginLeft: '-0.75rem'
  },
  docFileMediaSection: {
    position: 'relative',
    float: 'left',
    border: `1px solid ${DEFAULT_GREY_COLOR}`,
    borderRadius: '8px',
    backgroundColor: DEFAULT_DOC_FILE_BACKGROUND_COLOR,
    height: '55px',
    minWidth: '80px'
  },
  download: {
    position: 'absolute',
    top: 0,
    margin: '1rem',
    background: APP_PRIMARY_COLOR,
    borderRadius: '50%',
    right: 0,
    cursor: 'pointer',
    color: DEFAULT_WHITE_COLOR,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  audioDownload: {
    visibility: 'hidden',
    background: APP_PRIMARY_COLOR,
    borderRadius: '50%',
    cursor: 'pointer',
    color: DEFAULT_WHITE_COLOR,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  audioDownloadIcon: {
    visibility: 'visible !important' as any
  },
  downloadNew: {
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
    color: APP_PRIMARY_COLOR
  }
}));

//  TODO : update the interface types in an proper way
interface ViewMediaFileProps {
  clinicGroupId: number;
  clinicId: number;
  fileId: string | number;
  extension?: string;
  fileName: string;
  mainClass: string;
  mimeType: string;
  transcribe?: any;
  id?: any;
  mediaKey?: any;
  type?: string;
  ticketId?: number;
  module?: IAllPossibleTicketModules;
}
const ViewMediaFile = (props: ViewMediaFileProps): ReactElement => {
  const {
    clinicGroupId,
    fileId,
    mimeType,
    fileName,
    mainClass,
    clinicId,
    transcribe,
    id,
    mediaKey,
    type,
    ticketId,
    module
  } = props;
  const classes = useStyles();
  const {t} = useTranslation();
  const [fileUrl, setFileUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [showDownload, setShowDownload] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const fetchMediaFilesByMessage = async () => {
    try {
      let response = null;
      setLoading(true);
      if (type && ticketId) {
        if (!module) return;
        response = await getTicketAttachments({
          clinicGroupId,
          clinicId,
          patientTicketId: ticketId,
          attachmentId: fileId,
          params: {
            module: TICKETING_SYSTEM_MODULE_NAME_TO_MODULE_ID_MAPPER[module]
          }
        });
      } else {
        if (fileId) {
          // call the media file api only when there is a fileId
          response = await getMediaFile({
            clinicGroupId,
            fileId,
            clinicId
          });
        }
      }
      const blob = new Blob([new Uint8Array(response)]);
      const url = window.URL.createObjectURL(blob);
      setFileUrl(url);
      setLoading(false);
    } catch {
      setLoading(false);
      setError(t('COMMON.FILE_404'));
    }
  };

  useEffect(() => {
    fetchMediaFilesByMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  };

  const renderFilesInConversationDialog = (file: any) => {
    if (error !== '') {
      return <ErrorCard message={error} />;
    }
    if (mimeType?.startsWith(MimeTypeFileExtensions.IMAGE)) {
      return (
        <div className={classes.mediaSection}>
          <PhotoPreviewComponent src={file}>
            <img src={file} className={classes.content} alt="" />
          </PhotoPreviewComponent>
          {/* <img src={file} className={classes.content} /> */}
          {showDownload && (
            <Tooltip title={t('COMMON.DOWNLOAD') as string}>
              <GetAppIcon
                className={classes.download}
                onClick={() => {
                  download();
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    } else if (mimeType?.startsWith(MimeTypeFileExtensions.VIDEO)) {
      return (
        <div className={classes.mediaSection}>
          <video src={file} controls className={classes.content} />
          {showDownload && (
            <Tooltip title={t('COMMON.DOWNLOAD') as string}>
              <GetAppIcon
                className={classes.download}
                onClick={() => {
                  download();
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    } else if (mimeType?.startsWith(MimeTypeFileExtensions.AUDIO)) {
      return (
        <div
          className={[classes.mediaSection, classes.audioMediaSection].join(
            ' '
          )}>
          <AudioFileComponent
            src={file}
            className={classes.content}
            transcribe={transcribe}
          />
          {
            <Tooltip title={t('COMMON.DOWNLOAD') as string}>
              <GetAppIcon
                className={
                  id === mediaKey
                    ? [classes.audioDownload, classes.audioDownloadIcon].join(
                        ' '
                      )
                    : classes.audioDownload
                }
                onClick={() => {
                  download();
                }}
              />
            </Tooltip>
          }
        </div>
      );
    } else {
      return (
        <div className={classes.docFileMediaSection}>
          <DocFileComponent
            className={classes.docFileContent}
            avatarClassName={classes.docFileAvatarContent}
            fileName={fileName}
            fileNameClass={classes.docFileNameClass}
          />
          {showDownload && (
            <Tooltip title={t('COMMON.DOWNLOAD') as string}>
              <GetAppIcon
                className={classes.download}
                onClick={() => {
                  download();
                }}
              />
            </Tooltip>
          )}
        </div>
      );
    }
  };
  const handleViewImage = () => {
    if (mimeType?.startsWith(MimeTypeFileExtensions.IMAGE)) {
      setVisible(true); // Show the PhotoSlider component
    } else {
      download();
    }
  };

  const renderIconAndTitle = () => {
    return (
      <>
        <div className={classes.mediaSection} style={{cursor: 'pointer'}}>
          <div className={classes.mediaSection} onClick={handleViewImage}>
            {mimeType?.startsWith(MimeTypeFileExtensions.IMAGE) ? (
              <ImageIcon style={{color: '#e2bd4e'}} />
            ) : (
              <InsertDriveFileIcon style={{color: APP_PRIMARY_COLOR}} />
            )}
            <div>{fileName}</div>
          </div>
          {showDownload && (
            <Tooltip title={t('COMMON.DOWNLOAD') as string}>
              <GetAppIcon
                className={classes.downloadNew}
                onClick={() => {
                  download();
                }}
              />
            </Tooltip>
          )}
        </div>
        {/* PhotoSlider component */}
        <PhotoSlider
          images={[{src: fileUrl, key: fileId}]}
          visible={visible}
          onClose={() => setVisible(false)}
          index={index}
          onIndexChange={setIndex}
        />
      </>
    );
  };
  return (
    <div
      className={[mainClass, classes.mediaHeader].join(' ')}
      onMouseEnter={e => {
        setShowDownload(true);
      }}
      onMouseLeave={e => {
        setShowDownload(false);
      }}>
      {loading && (
        <Loader
          style={{color: APP_PRIMARY_COLOR}}
          color={'secondary'}
          size={32}
        />
      )}
      {type && fileUrl ? renderIconAndTitle() : ''}
      {!type && fileUrl && renderFilesInConversationDialog(fileUrl)}
    </div>
  );
};

export default ViewMediaFile;
